import React from 'react';

/**
 * RouteErrorBoundary
 * Catches runtime and lazy chunk loading errors specifically within routes
 * without crashing the entire app shell, providers, or authentication state.
 */
export class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[RouteErrorBoundary] Unhandled route error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      const isArabic =
        typeof document !== 'undefined' &&
        (document.documentElement.lang?.startsWith('ar') || document.documentElement.dir === 'rtl');

      return (
        <div
          role="alert"
          dir={isArabic ? 'rtl' : 'ltr'}
          className="flex min-h-[50vh] w-full items-center justify-center p-6 text-slate-800 dark:text-slate-100"
        >
          <div className="w-full max-w-md rounded-3xl border border-slate-200 dark:border-white/10 bg-white dark:bg-page-surface p-8 text-center shadow-xl backdrop-blur-xl">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-500/10 text-amber-500 dark:bg-amber-400/10 dark:text-amber-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-7 w-7"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>

            <h3 className="mb-2 text-lg font-bold text-slate-900 dark:text-white">
              {isArabic ? 'تعذر تحميل الصفحة' : 'Unable to load page'}
            </h3>

            <p className="mb-6 text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
              {isArabic
                ? 'حدث انقطاع مؤقت أثناء تحميل محتوى هذه الصفحة. يرجى إعادة المحاولة أو تحديث الصفحة.'
                : 'A temporary interruption occurred while loading this page. Please try again or refresh.'}
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={this.handleRetry}
                className="flex-1 rounded-xl bg-primary-accent px-4 py-2.5 text-sm font-semibold text-white shadow-md hover:opacity-90 active:scale-95 transition-all cursor-pointer"
              >
                {isArabic ? 'إعادة المحاولة' : 'Try Again'}
              </button>
              <button
                type="button"
                onClick={this.handleReload}
                className="flex-1 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-100 dark:bg-white/5 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-white/10 active:scale-95 transition-all cursor-pointer"
              >
                {isArabic ? 'تحديث الصفحة' : 'Reload Page'}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default RouteErrorBoundary;
